package link.erpango.quip.core.interactor

import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import link.erpango.quip.QuipConstants
import link.erpango.quip.data.db.entities.ICatalogTable
import link.erpango.quip.data.db.entities.ICommonValuesTable
import link.erpango.quip.data.remote.repository.extraction.IExtractionRepository
import link.erpango.quip.domain.catalog.DefaultSparePartCatalog
import link.erpango.quip.domain.entities.DExtractionError
import link.erpango.quip.domain.entities.DExtractionOutcome
import link.erpango.quip.domain.entities.DExtractionStatus
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.extraction.parseNote
import link.erpango.quip.domain.noteimport.DraftContext
import link.erpango.quip.domain.noteimport.ImportDraft
import link.erpango.quip.domain.noteimport.NoteImportEvent
import link.erpango.quip.domain.noteimport.NoteImportLimits
import link.erpango.quip.domain.noteimport.matchGarage
import link.erpango.quip.domain.noteimport.mergeDrafts
import link.erpango.quip.domain.noteimport.toImportDraft
import link.erpango.quip.domain.usecases.INoteImportUseCase
import link.erpango.quip.domain.usecases.NoteImportAiResult
import link.erpango.quip.utils.FileUtils
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * Implementation of [INoteImportUseCase]. Dependencies are narrow on purpose (catalog and
 * common-values tables, the extraction repository, a garage provider) so tests can use
 * in-memory fakes.
 */
@OptIn(ExperimentalTime::class)
class NoteImportInteractor(
    private val extraction: IExtractionRepository,
    private val catalog: ICatalogTable,
    private val commonValues: ICommonValuesTable,
    private val garage: suspend () -> List<DVehicle>,
    private val logEvent: (name: String, param: String) -> Unit,
    private val sparePartCatalog: DSparePartCatalog = DefaultSparePartCatalog,
    private val today: () -> LocalDate = { Clock.System.todayIn(TimeZone.currentSystemDefault()) },
    private val parserDispatcher: CoroutineDispatcher = Dispatchers.Default,
) : INoteImportUseCase {

    private data class VehicleCatalog(val brands: List<String>, val modelsByBrand: Map<String, List<String>>)

    private val catalogMutex = Mutex()
    private var cachedCatalog: VehicleCatalog? = null

    override suspend fun analyzeLocally(note: String): ImportDraft {
        val text = note.take(NoteImportLimits.MAX_NOTE_LENGTH)
        if (text.isBlank()) return ImportDraft()
        val ctx = draftContext()
        val vehicleCatalog = vehicleCatalog()
        val draft = withContext(parserDispatcher) {
            parseNote(text, vehicleCatalog.brands, vehicleCatalog.modelsByBrand).toImportDraft(ctx)
        }
        return draft.matchGarage(garageOrEmpty())
    }

    override suspend fun improveWithAi(note: String, local: ImportDraft): NoteImportAiResult {
        if (note.isBlank()) return NoteImportAiResult.Failure(DExtractionError.EmptyNote)
        if (note.length > NoteImportLimits.MAX_NOTE_LENGTH) {
            return NoteImportAiResult.Failure(DExtractionError.NoteTooLong)
        }
        return when (val outcome = extraction.extract(note)) {
            is DExtractionOutcome.Failure -> NoteImportAiResult.Failure(outcome.error)
            is DExtractionOutcome.Success -> {
                val aiDraft = outcome.response.toImportDraft(draftContext())
                NoteImportAiResult.Success(mergeDrafts(local, aiDraft).matchGarage(garageOrEmpty()))
            }
        }
    }

    override suspend fun aiStatus(): DExtractionStatus? = extraction.status()

    override suspend fun hasAiConsent(): Boolean =
        commonValues.selectValue(QuipConstants.CommonValues.NoteImport.aiConsent) == CONSENT_GRANTED

    override suspend fun setAiConsent(granted: Boolean) {
        val key = QuipConstants.CommonValues.NoteImport.aiConsent
        if (granted) {
            commonValues.insertValue(key, CONSENT_GRANTED, Clock.System.now().toEpochMilliseconds())
        } else {
            commonValues.deleteValue(key)
        }
    }

    override fun track(event: NoteImportEvent) {
        try {
            logEvent(event.name, event.param)
        } catch (e: Exception) {
            FileUtils.log("note-import: analytics failed ${e::class.simpleName}")
        }
    }

    private suspend fun draftContext(): DraftContext =
        DraftContext(today = today(), brands = vehicleCatalog().brands, sparePartCatalog = sparePartCatalog)

    /** Synced catalog, or the bundled one when it has not been downloaded yet. Cached per session. */
    private suspend fun vehicleCatalog(): VehicleCatalog = catalogMutex.withLock {
        cachedCatalog ?: run {
            val brands = runCatching { catalog.selectAllBrandNames() }.getOrDefault(emptyList())
            val models = runCatching { catalog.selectModelNamesGroupedByBrand() }.getOrDefault(emptyMap())
            val loaded = if (brands.isEmpty()) {
                VehicleCatalog(
                    QuipConstants.VehicleAttributes.VehicleBrands,
                    QuipConstants.VehicleAttributes.VehicleModelsByBrand,
                )
            } else {
                VehicleCatalog(brands, models)
            }
            loaded.also { cachedCatalog = it }
        }
    }

    private suspend fun garageOrEmpty(): List<DVehicle> = runCatching { garage() }.getOrDefault(emptyList())

    private companion object {
        const val CONSENT_GRANTED = "granted"
    }
}
